import axios from "axios";
import { getRecaptchaToken } from "./recaptcha";
import { redactAuthorization } from "./common/utils/redact_authorization";
import { describeError } from "./common/utils/error_message";

const axiosInstance = axios.create({
	baseURL: import.meta.env.VITE_API,
});

const PROTECTED_PREFIXES = ["/bible/verses/ai-lookup"];
const DEFAULT_ACTION = "GLOBAL_PROTECTED";

function isOurApiUrl(url) {
	try {
		const base = axiosInstance.defaults.baseURL || window.location.origin;
		const u = new URL(url || "", base);
		return u.host === new URL(base).host;
	} catch {
		return false;
	}
}

function getPathname(url) {
	try {
		const base = axiosInstance.defaults.baseURL || window.location.origin;
		return new URL(url || "", base).pathname || "";
	} catch {
		return "";
	}
}

axiosInstance.interceptors.request.use(async cfg => {
	const method = (cfg.method || "").toLowerCase();

	if (!isOurApiUrl(cfg.url)) return cfg;

	if (method === "options") return cfg;

	cfg.headers = cfg.headers || {};

	const token = localStorage.getItem("access_token");
	if (token) cfg.headers.Authorization = `Bearer ${token}`;
	else delete cfg.headers.Authorization;

	if (cfg.skipRecaptcha) return cfg;

	const path = getPathname(cfg.url);
	const needsRecaptcha = PROTECTED_PREFIXES.some(p => path.startsWith(p));
	if (!needsRecaptcha) return cfg;

	try {
		const action = cfg.recaptchaAction || DEFAULT_ACTION;
		const reToken = await getRecaptchaToken(action);
		if (!reToken) throw new Error("empty token");
		cfg.headers["recaptcha-token"] = reToken;
		return cfg;
	} catch (e) {
		console.error("[reCAPTCHA] failed:", describeError(e));
		const err = new Error("RECAPTCHA_TOKEN_FAILED");
		err.name = "RECAPTCHA_TOKEN_FAILED";
		err.cause = e;
		return Promise.reject(err);
	}
});

// Every failure leaves through here, so the token is gone from the error before any
// catch block can hand it to the console. See common/utils/redact_authorization.
axiosInstance.interceptors.response.use(
	response => response,
	error => Promise.reject(redactAuthorization(error)),
);

export default axiosInstance;
